import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import {
  Component,
  OnDestroy,
  ElementRef,
  ViewChildren,
  QueryList,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ProfileSettingImpl, ProfileUpdateRequestImpl } from "shared/cubiq-model-multi-asset";
import { BfxUserSettingsService, BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import {
  BfxUserSettingsPageComponent,
  BfxUserSettingsValidityState,
} from "./bfx-user-settings-page.component";
import { IBfxUserSettingsPageSubComponent } from "app/bfx-user-settings/bfx-user-settings-page.component";
import {
  SystemConfigPromiseService,
  ProfilePromiseActionService,
  UserPromiseLookupService,
} from "shared/cubiq-services";
import { BfxPopupNotificatonSettingsService } from "app/bfx-common/services/bfx-popup-notifications-settings.service";
import {
  APP_BMARK_KEY,
  APP_KEY,
  BfxUserNotificationSettingsService,
  POPUP_BMARK_KEY,
  POPUP_KEY,
} from "app/bfx-user-settings/services/bfx-user-notification-settings.service";
import { BfxPopupEventSetting } from "app/bfx-user-settings/model/bfx-popup-event-setting";
import { BfxAppEventSetting } from "app/bfx-user-settings/model/bfx-app-event-setting";

@Component({
  selector: ".bfx-notification-settings",
  templateUrl: "./html/bfx-notification-settings.html",
  providers: [BfxUserNotificationSettingsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxNotificationSettingsComponent
  extends BfxUserSettingsPageComponent
  implements OnDestroy
{
  public tableHeight: number = 240;
  public activeSubComponent: number = 0;
  public panelHeight: number = 0;
  public dataChanged: boolean = false;

  public algosEnabled: boolean = false;
  public bmarksEnabled: boolean = false;

  public audioDisabled: boolean = false;

  private savedSettingsMap: Map<string, string> = new Map();
  @ViewChildren("subComponent") subComponents: QueryList<
    IBfxUserSettingsPageSubComponent<BfxNotificationSettingsComponent>
  >;

  constructor(
    public userSettingsService: BfxUserSettingsService,
    public systemConfigService: SystemConfigPromiseService,
    public profileService: ProfilePromiseActionService,
    public userService: UserPromiseLookupService,
    public element: ElementRef,
    public translationService: TranslateService,
    private settingsService: BfxUserNotificationSettingsService,
    private cd: ChangeDetectorRef,
  ) {
    super();
    Promise.all([
      this.systemConfigService.areBenchmarksEnabled(),
      this.systemConfigService.areAlgosEnabled(),
      settingsService.getAudioDisabled(),
    ]).then(([bmarksEnabled, algosEnabled, audioDisabled]) => {
      this.bmarksEnabled = bmarksEnabled;
      this.algosEnabled = algosEnabled;

      //For the moment, just pull the current disableAudio value form the first setting we find and
      // push it down
      this.audioDisabled = audioDisabled;
      this.cd.detectChanges();
      this.initialize();
    });
  }

  public onTabActivated(event: any): void {
    this.activeSubComponent = event.index;
    this.cd.markForCheck();
  }

  public initialize(): void {
    // BfxUserSettingsTabConfig.ngAfterViewInit() has already ran so ngIf components were missed
    Promise.all(
      this.subComponents.map((subComponent) => {
        if (subComponent.categoryComponent == null) subComponent.categoryComponent = this;
        if (subComponent.parentComponent == null)
          subComponent.parentComponent = this.parentComponent;

        return subComponent.initialize();
      }),
    ).then(() => this.cd.markForCheck());
  }

  ngOnDestroy() {}

  public validate(): Promise<BfxUserSettingsValidityState> {
    let promises: Promise<BfxUserSettingsValidityState>[] = [];

    this.subComponents.forEach((subComponent) => {
      let promise = subComponent.validate();

      promises.push(promise);
    });

    return new Promise<BfxUserSettingsValidityState>((res, reject) => {
      Promise.all(promises).then(
        (values) => {
          let subComponentHasInvalid: boolean = values.some((value) => {
            return value === BfxUserSettingsValidityState.INVALID;
          });
          let subComponentHasActiveUnsaveble: boolean = values.some((value) => {
            return value === BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
          });
          let state;

          if (subComponentHasInvalid) {
            state = BfxUserSettingsValidityState.INVALID;
          } else if (subComponentHasActiveUnsaveble) {
            state = BfxUserSettingsValidityState.ACTIVE_UNSAVEABLE;
          } else {
            let subComponentValid: boolean = values.every((value) => {
              return value === BfxUserSettingsValidityState.VALID;
            });
            state = subComponentValid
              ? BfxUserSettingsValidityState.VALID
              : BfxUserSettingsValidityState.INVALID;
          }

          //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);

          res(state);
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  public reset(): Promise<void> {
    // First we reset the data in the service then we get all the subcomponents to reread the service
    const resetPromise = this.settingsService.resetSettings();
    return new Promise<void>((res, reject) => {
      const promises: Promise<void>[] = [];
      resetPromise.then(() => {
        this.subComponents.forEach((subComponent) => {
          const promise = subComponent.reset();
          promises.push(promise);
        });

        Promise.all(promises).then(
          () => {
            // run any reset logic for this.reset now
            // as subComponents did not reject reset

            res();
          },
          () => {
            reject();
          },
        );
      });
    });
  }

  /**
   * Handle a save from parent component
   * @param request
   */
  public save(request: ProfileUpdateRequestImpl): void {
    this.subComponents.forEach((subComponent) => {
      subComponent.save(request);
    });

    this.savedSettingsMap.clear();
    // Get each key JSON and add to the request
    this.saveSettings(request, POPUP_KEY, this.settingsService.getSavePopupSettings(false));
    this.saveSettings(request, POPUP_BMARK_KEY, this.settingsService.getSavePopupSettings(true));
    this.saveSettings(request, APP_KEY, this.settingsService.getSaveAppSettings(false));
    this.saveSettings(request, APP_BMARK_KEY, this.settingsService.getSaveAppSettings(true));
  }

  /**
   * Converts the list of settings to a JSON and stor inside the request
   * The JSON is also stored in local map for applying to UserProfile if save is successful
   * @param request
   * @param profileKey
   * @param settings
   */
  private saveSettings(
    request: ProfileUpdateRequestImpl,
    profileKey: string,
    settings: BfxPopupEventSetting[] | BfxAppEventSetting[],
  ): void {
    let profileSetting: ProfileSettingImpl = this.getProfileSettingFromKey(profileKey, request);
    if (!profileSetting) {
      profileSetting = new ProfileSettingImpl();
      profileSetting.profileSettingKey = profileKey;
      request.settings.push(profileSetting);
    }
    settings.forEach((setting) => (setting.disableAllAudio = this.audioDisabled));
    profileSetting.profileSettingValue = JSON.stringify(settings);
    this.savedSettingsMap.set(profileKey, profileSetting.profileSettingValue);
  }

  /**
   *
   * @param profileSettingKey
   * @param request
   * @returns
   */
  private getProfileSettingFromKey(
    profileSettingKey: string,
    request: ProfileUpdateRequestImpl,
  ): ProfileSettingImpl {
    let profileSettings: Array<any> = request.settings;
    if (!profileSettings) return;
    let savedPopUpSetting: ProfileSettingImpl = profileSettings.find((setting) => {
      return setting.profileSettingKey == profileSettingKey;
    });
    return savedPopUpSetting;
  }

  public postReset(): void {
    this.dataChanged = false;

    this.cd.markForCheck();

    this.subComponents.forEach((subComponent) => {
      subComponent.postReset();
    });
  }

  /**
   * when save is successful, then we must apply the saved profile key values back into the activeuser in order
   * for them to be picked up by interested services
   * @param request
   */
  public postSave(request: ProfileUpdateRequestImpl): void {
    let activeUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      for (const [key, value] of this.savedSettingsMap.entries()) {
        activeUser.profileSettings.set(key, value);
      }
    }
    this.savedSettingsMap.clear();
    this.dataChanged = false;
    this.cd.markForCheck();
    this.subComponents.forEach((subComponent) => {
      subComponent.postSave(request);
    });
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {
    this.panelHeight = componentState.componentBounds.height - 120;

    this.subComponents.forEach((subComponent) => {
      // onComponentStateChange is optional in interface
      if (typeof subComponent.onComponentStateChanged === "function") {
        subComponent.onComponentStateChanged(componentState);
      }
    });
  }

  public audioChanged($event: boolean) {
    this.audioDisabled = $event;
    this.dataChanged = true;
    this.cd.markForCheck();
    if (this.parentComponent) {
      this.parentComponent.validate(this);
    }
  }
}
